import { Component } from 'react';

import { captureException, withScope } from '@sentry/nextjs';

import { PanelErrorBoundaryProvider } from './PanelErrorBoundaryContext';
import PanelErrorFallback from './PanelErrorFallback';

import type { ErrorInfo, ReactNode } from 'react';

interface PanelErrorBoundaryProps {
  children: ReactNode;
  /** 패널 닫기 핸들러 (fallback 헤더의 닫기 버튼에 연결). 패널은 목록으로 리다이렉트할 수 없으므로 닫기로 종료한다. */
  onClose?: () => void;
  /** panelId 등 항목 식별자 — 변경 시 에러 상태를 초기화해 다른 항목 선택 시 자동 복구한다. */
  resetKey?: string;
}

interface PanelErrorBoundaryState {
  // throw된 값은 Error 인스턴스가 아닐 수 있다. axios onRejected가 spread({ ...error })로
  // 재구성한 게이팅 에러는 Error 프로토타입을 잃은 plain 객체이지만 response.status는 보존하므로,
  // 원형을 보존해 fallback이 getHttpStatus로 status(403 등)를 추출할 수 있게 unknown으로 받는다.
  // 초기/복구(에러 없음) 상태는 null sentinel로 표현하며 render의 if(error)에서 falsy로 걸러진다.
  error: unknown;
}

/**
 * 패널 컨텐츠 렌더 중 throw된 에러를 잡아 인플레이스 에러 화면으로 교체하는 ErrorBoundary.
 *
 * @description
 * - DetailContentGate(패널 종착)는 게이팅 에러를 throw하지 않고 PanelErrorFallback을 직접 렌더한다.
 *   이 바운더리는 그 경로와 무관하게, 패널 콘텐츠에서 실제로 던져진 예기치 못한 렌더 예외를 잡는 안전망이다.
 * - resetKey(패널 항목 키)가 바뀌면 에러 상태를 초기화해 정상 렌더를 재시도한다.
 *   (resetKey를 전달하지 않거나 불변이면 한 번 잡힌 에러는 재마운트 전까지 유지된다 — 현재 사용처는 항상 전달한다.)
 * - 에러는 Sentry로 캡처한다(기존 AppBoundary/ErrorBoundary 태깅 패턴 재사용).
 */
class PanelErrorBoundary extends Component<PanelErrorBoundaryProps, PanelErrorBoundaryState> {
  public state: PanelErrorBoundaryState = {
    error: null,
  };

  public static getDerivedStateFromError(error: unknown): PanelErrorBoundaryState {
    return { error };
  }

  public componentDidCatch(error: unknown, errorInfo: ErrorInfo): void {
    withScope(scope => {
      scope.setTags({
        errorType: 'Panel Render Error',
        errorOrigin: 'panel',
        requestServerName: location.origin,
        path: location.pathname,
      });
      // 렌더 에러 디버깅에 가장 유용한 React 컴포넌트 스택을 함께 전송한다.
      scope.setExtra('componentStack', errorInfo.componentStack);
      captureException(error);
    });
  }

  public componentDidUpdate(prevProps: PanelErrorBoundaryProps): void {
    if (prevProps.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: null });
    }
  }

  public render() {
    const { error } = this.state;
    const { children, onClose } = this.props;

    return (
      <PanelErrorBoundaryProvider value={{ onClose }}>
        {error ? <PanelErrorFallback error={error} onClose={onClose} /> : children}
      </PanelErrorBoundaryProvider>
    );
  }
}

export default PanelErrorBoundary;
